import { useCallback, useEffect, useState } from 'react';
import MentorPortalNav from './MentorPortalNav';
import MentorSessionsChrome from './MentorSessionsChrome';
import MentorSessionsHeader from './MentorSessionsHeader';
import MentorSessionsTabs, { SessionsSubTab } from './MentorSessionsTabs';
import MentorAvailabilityPanel, {
    availabilityStartMinutesForDuration,
    slotKey,
} from './MentorAvailabilityPanel';
import MentorUpcomingBookings from './MentorUpcomingBookings';
import MentorPastSessionsPanel from './MentorPastSessionsPanel';
import MentorLogEventModal from './MentorLogEventModal';
import MentorPortalService, {
    MentorAvailabilityPayload,
    MentorAvailabilitySlot,
    MentorBookedSlot,
    MentorMenteeCard,
    MentorPastSessionRow,
    MentorUpcomingBooking,
} from './MentorPortalService';
import { EMPTY_MENTOR_COPY } from './mentorDashboardFormat';
import './mentorDashboard.css';
import './mentorSessions.css';

const DEFAULT_AVAILABILITY: MentorAvailabilityPayload = {
    slotDurationMinutes: 45,
    slots: [],
};

const MentorSessions = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const [tab, setTab] = useState<SessionsSubTab>('availability');
    const [mentees, setMentees] = useState<MentorMenteeCard[]>([]);
    const [saved, setSaved] = useState<MentorAvailabilityPayload>(DEFAULT_AVAILABILITY);
    const [bookedSlots, setBookedSlots] = useState<MentorBookedSlot[]>([]);
    const [durationMinutes, setDurationMinutes] = useState(45);
    const [slots, setSlots] = useState<MentorAvailabilitySlot[]>([]);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [sessions, setSessions] = useState<MentorPastSessionRow[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pastLoading, setPastLoading] = useState(false);
    const [upcomingBookings, setUpcomingBookings] = useState<MentorUpcomingBooking[]>([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [upcomingError, setUpcomingError] = useState<string | null>(null);
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logPairId, setLogPairId] = useState<number | null>(null);

    const applyAvailability = (payload: MentorAvailabilityPayload) => {
        const duration = payload.slotDurationMinutes ?? 45;
        const allowed = new Set(availabilityStartMinutesForDuration(duration));
        const alignedSlots = (payload.slots || []).filter((slot) => allowed.has(slot.startMinute));
        const next: MentorAvailabilityPayload = {
            slotDurationMinutes: duration,
            slots: alignedSlots,
        };
        setSaved(next);
        setDurationMinutes(next.slotDurationMinutes);
        setSlots(next.slots);
        setBookedSlots(payload.bookedSlots || []);
    };

    const loadUpcoming = useCallback(() => {
        setUpcomingLoading(true);
        setUpcomingError(null);
        MentorPortalService.listSessions({ page: 1, pageSize: 25 })
            .then((data) => {
                setUpcomingBookings(data.upcomingBookings || []);
            })
            .catch((err: { status?: number; message?: string }) => {
                if (err?.status === 403 || err?.status === 401) {
                    setForbidden(true);
                    return;
                }
                setUpcomingError(err?.message || 'Unable to load upcoming bookings.');
            })
            .finally(() => setUpcomingLoading(false));
    }, []);

    const loadPast = useCallback((nextPage: number) => {
        setPastLoading(true);
        MentorPortalService.listSessions({ page: nextPage, pageSize: 25 })
            .then((data) => {
                setSessions(data.sessions || []);
                setPage(data.pagination.page);
                setPageSize(data.pagination.pageSize);
                setTotal(data.pagination.total);
                setTotalPages(data.pagination.totalPages);
            })
            .catch((err: { status?: number; message?: string }) => {
                if (err?.status === 403 || err?.status === 401) {
                    setForbidden(true);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => setPastLoading(false));
    }, []);

    useEffect(() => {
        let cancelled = false;
        Promise.all([MentorPortalService.getMentees(), MentorPortalService.getAvailability()])
            .then(([menteePayload, availability]) => {
                if (cancelled) return;
                setMentees(menteePayload.mentees || []);
                applyAvailability(availability);
                setLoadError(null);
                setForbidden(false);
            })
            .catch((err: { status?: number; message?: string }) => {
                if (cancelled) return;
                if (err?.status === 403 || err?.status === 401) {
                    setForbidden(true);
                    setLoadError(null);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (tab === 'past') {
            loadPast(1);
        }
        if (tab === 'upcoming') {
            loadUpcoming();
        }
    }, [tab, loadPast, loadUpcoming]);

    const canLogOffline = mentees.some((row) => row.canLogOffline === true);
    const isEmptyForbidden = !loading && !loadError && forbidden;

    const onToggle = (dayOfWeek: number, startMinute: number) => {
        const key = slotKey(dayOfWeek, startMinute);
        setSlots((current) => {
            const exists = current.some((slot) => slotKey(slot.dayOfWeek, slot.startMinute) === key);
            if (exists) {
                return current.filter((slot) => slotKey(slot.dayOfWeek, slot.startMinute) !== key);
            }
            return [...current, { dayOfWeek, startMinute }];
        });
        setSaveError(null);
    };

    const onSave = () => {
        setIsSaving(true);
        setSaveError(null);
        MentorPortalService.putAvailability({
            slotDurationMinutes: durationMinutes,
            slots,
        })
            .then((stored) => {
                applyAvailability({ ...stored, bookedSlots });
            })
            .catch((err: unknown) => {
                setSaveError(MentorPortalService.errorMessage(err));
            })
            .finally(() => setIsSaving(false));
    };

    const onDiscard = () => {
        setDurationMinutes(saved.slotDurationMinutes);
        setSlots(saved.slots || []);
        setSaveError(null);
    };

    const reloadAfterLog = () => {
        MentorPortalService.getMentees()
            .then((data) => setMentees(data.mentees || []))
            .catch(() => undefined);
        if (tab === 'past') {
            loadPast(page);
        }
    };

    return (
        <div className="container py-4 mdash">
            
            <MentorPortalNav />
            <MentorSessionsChrome />
            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && loadError && <p className="mdash-empty">{loadError}</p>}
            {isEmptyForbidden && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {!loading && !loadError && !forbidden && (
                <>
                    <MentorSessionsHeader
                        canLogOffline={canLogOffline}
                        onLogOffline={() => {
                            setLogPairId(null);
                            setLogModalOpen(true);
                        }}
                    />
                    <MentorSessionsTabs active={tab} onChange={setTab} />
                    {tab === 'availability' && (
                        <MentorAvailabilityPanel
                            durationMinutes={durationMinutes}
                            slots={slots}
                            saved={saved}
                            bookedSlots={bookedSlots}
                            saveError={saveError}
                            isSaving={isSaving}
                            onDurationChange={(value) => {
                                const allowed = new Set(availabilityStartMinutesForDuration(value));
                                setDurationMinutes(value);
                                setSlots((current) =>
                                    current.filter((slot) => allowed.has(slot.startMinute))
                                );
                                setSaveError(null);
                            }}
                            onToggle={onToggle}
                            onSave={onSave}
                            onDiscard={onDiscard}
                        />
                    )}
                    {tab === 'upcoming' && (
                        <MentorUpcomingBookings
                            bookings={upcomingBookings}
                            mentees={mentees}
                            isLoading={upcomingLoading}
                            loadError={upcomingError}
                        />
                    )}
                    {tab === 'past' && (
                        <MentorPastSessionsPanel
                            sessions={sessions}
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            totalPages={totalPages}
                            isLoading={pastLoading}
                            onPage={loadPast}
                        />
                    )}
                    <MentorLogEventModal
                        show={logModalOpen}
                        mentees={mentees}
                        preselectedPairId={logPairId}
                        onClose={() => setLogModalOpen(false)}
                        onLogged={reloadAfterLog}
                    />
                </>
            )}
        </div>
    );
};

export default MentorSessions;
